import { computed, nextTick, onMounted, onUpdated, reactive, ref, toRef, watch, watchEffect } from 'vue';

import { useState } from '@components/composable';
import { convertToUnit, getTargetScrollBarSize, isVisible } from '@components/utils';

import { INTERNAL_HOOKS } from '../constants';
import { useProvideSticky } from '../context';
import { getColumnsKey, validateValue } from '../utils';

import { useLayoutState, useTimeoutLock } from './frame';
import { useSticky, useStickyOffsets } from './sticky';

import type { ITableProps } from './table';
import type { NTable } from '../interface';
import type { CSSProperties, ComputedRef } from 'vue';

// Used for customize scroll
const EMPTY_SCROLL_TARGET = {};

export function useScroll<RecordType>(
  props: ITableProps,
  flattenColumns: ComputedRef<readonly NTable.NColumn.IColumn<RecordType>[]>
) {
  const componentWidth = ref(0);
  const fullTableRef = ref<HTMLDivElement>();
  const scrollHeaderRef = ref<HTMLDivElement>();
  const scrollBodyRef = ref<HTMLDivElement>();
  const scrollBodySizeInfo = ref<{ scrollWidth: number; clientWidth: number }>({
    scrollWidth: 0,
    clientWidth: 0
  });
  const scrollSummaryRef = ref<HTMLDivElement>();

  const [pingedLeft, setPingedLeft] = useState(false);
  const [pingedRight, setPingedRight] = useState(false);
  const [colsWidths, updateColsWidths] = useLayoutState(new Map<NTable.TKey, number>());

  // Convert map to number width
  const colsKeys = computed(() => getColumnsKey(flattenColumns.value));
  const colWidths = computed(() => colsKeys.value.map((columnKey) => colsWidths.value.get(columnKey)));
  const columnCount = computed(() => flattenColumns.value.length);
  const stickyOffsets = useStickyOffsets(colWidths, columnCount);
  const fixHeader = computed(() => props.scroll && validateValue(props.scroll.y));
  const horizonScroll = computed(() => (props.scroll && validateValue(props.scroll.x)) || Boolean(props.expandFixed));
  const fixColumn = computed(() => horizonScroll.value && flattenColumns.value.some(({ fixed }) => fixed));

  // Sticky
  const stickyRef = ref<{ setScrollLeft: (left: number) => void }>();
  const stickyState = useSticky(toRef(props, 'sticky'));

  const summaryFixedInfo = reactive<Record<string, boolean | string>>({});
  const fixFooter = computed(() => {
    const [info] = Object.values(summaryFixedInfo);
    return (fixHeader.value || stickyState.value.isSticky) && info;
  });

  const summaryCollect = (uniKey: string, fixed: boolean | string) => {
    if (fixed) {
      summaryFixedInfo[uniKey] = fixed;
    } else {
      delete summaryFixedInfo[uniKey];
    }
  };

  // Scroll
  const scrollXStyle = ref<CSSProperties>({});
  const scrollYStyle = ref<CSSProperties>({});
  const scrollTableStyle = ref<CSSProperties>({});

  watchEffect(() => {
    if (fixHeader.value) {
      scrollYStyle.value = {
        overflowY: 'scroll',
        maxHeight: convertToUnit(props.scroll!.y, 'px')
      };
    }

    if (horizonScroll.value) {
      scrollXStyle.value = { overflowX: 'auto' };

      if (!fixHeader.value) {
        scrollYStyle.value = { overflowY: 'hidden' };
      }

      scrollTableStyle.value = {
        width: props.scroll!.x === true ? 'auto' : convertToUnit(props.scroll!.x, 'px'),
        minWidth: '100%'
      };
    }
  });

  const onColumnResize = (columnKey: NTable.TKey, width: number) => {
    if (isVisible(fullTableRef.value!)) {
      updateColsWidths((widths) => {
        if (widths.get(columnKey) !== width) {
          const newWidths = new Map(widths);
          newWidths.set(columnKey, width);
          return newWidths;
        }
        return widths;
      });
    }
  };

  const [setScrollTarget, getScrollTarget] = useTimeoutLock<HTMLElement | null>(null);

  function forceScroll(scrollLeft: number, target?: HTMLElement | ((left: number) => void)) {
    if (!target) return;

    if (typeof target === 'function') {
      target(scrollLeft);
      return;
    }

    const domTarget = (target as any).$el || target;
    if (domTarget.scrollLeft !== scrollLeft) {
      domTarget.scrollLeft = scrollLeft;
    }
  }

  const onScroll: (...args: any[]) => void = ({
    currentTarget,
    scrollLeft
  }: {
    currentTarget: HTMLElement;
    scrollLeft?: number;
  }) => {
    const mergedScrollLeft = typeof scrollLeft === 'number' ? scrollLeft : currentTarget.scrollLeft;
    const compareTarget = currentTarget || EMPTY_SCROLL_TARGET;

    if (!getScrollTarget() || getScrollTarget() === compareTarget) {
      setScrollTarget(compareTarget);

      forceScroll(mergedScrollLeft, scrollHeaderRef.value);
      forceScroll(mergedScrollLeft, scrollBodyRef.value);
      forceScroll(mergedScrollLeft, scrollSummaryRef.value);
      forceScroll(mergedScrollLeft, stickyRef.value?.setScrollLeft);
    }

    if (currentTarget) {
      const { scrollWidth, clientWidth } = currentTarget;

      setPingedLeft(mergedScrollLeft > 0);
      setPingedRight(mergedScrollLeft < scrollWidth - clientWidth);
    }
  };

  const triggerOnScroll = () => {
    if (horizonScroll.value && scrollBodyRef.value) {
      onScroll({ currentTarget: scrollBodyRef.value });
    } else {
      setPingedLeft(false);
      setPingedRight(false);
    }
  };

  let timeout: any;

  const updateWidth = (width: number) => {
    if (width !== componentWidth.value) {
      triggerOnScroll();
      componentWidth.value = fullTableRef.value ? fullTableRef.value?.offsetWidth : width;
    }
  };

  const onFullTableResize = ({ width }: { width: number }) => {
    clearTimeout(timeout);

    if (componentWidth.value === 0) {
      updateWidth(width);
      return;
    }

    timeout = setTimeout(() => {
      updateWidth(width);
    }, 100);
  };

  watch(
    [horizonScroll, () => props.data, () => props.columns],
    () => {
      if (horizonScroll.value) {
        triggerOnScroll();
      }
    },
    { flush: 'post' }
  );

  const [scrollbarSize, setScrollbarSize] = useState(0);

  useProvideSticky();

  onMounted(() => {
    nextTick(() => {
      triggerOnScroll();
      setScrollbarSize(getTargetScrollBarSize(scrollBodyRef.value!).width);
      scrollBodySizeInfo.value = {
        scrollWidth: scrollBodyRef.value?.scrollWidth || 0,
        clientWidth: scrollBodyRef.value?.clientWidth || 0
      };
    });
  });

  onUpdated(() => {
    nextTick(() => {
      const scrollWidth = scrollBodyRef.value?.scrollWidth || 0;
      const clientWidth = scrollBodyRef.value?.clientWidth || 0;

      if (
        scrollBodySizeInfo.value.scrollWidth !== scrollWidth ||
        scrollBodySizeInfo.value.clientWidth !== clientWidth
      ) {
        scrollBodySizeInfo.value = { scrollWidth, clientWidth };
      }
    });
  });

  watchEffect(
    () => {
      if (props.internalHooks === INTERNAL_HOOKS && props.internalRefs) {
        props.onUpdateInternalRefs &&
          props.onUpdateInternalRefs({
            body: scrollBodyRef.value ? (scrollBodyRef.value as any).$el || scrollBodyRef.value : null
          });
      }
    },
    { flush: 'post' }
  );

  const mergedTableLayout = computed(() => {
    if (props.tableLayout) return props.tableLayout;
    if (fixColumn.value) return props.scroll!.x === 'max-content' ? 'auto' : 'fixed';
    if (fixHeader.value || stickyState.value.isSticky || flattenColumns.value.some(({ ellipsis }) => ellipsis))
      return 'fixed';

    return 'auto';
  });

  return {
    fullTableRef,
    scrollHeaderRef,
    scrollBodyRef,
    scrollSummaryRef,
    stickyRef,

    componentWidth,
    fixHeader,
    fixColumn,
    fixFooter,
    pingedLeft,
    pingedRight,
    horizonScroll,
    mergedTableLayout,
    stickyOffsets,
    stickyState,
    scrollbarSize,
    colWidths,
    columnCount,
    scrollXStyle,
    scrollYStyle,
    scrollTableStyle,
    scrollBodySizeInfo,

    summaryCollect,
    onColumnResize,
    onFullTableResize,
    onScroll
  };
}
